import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { StripeCardField } from '@components/stripe/stripe-card-field';
import {
  useSubscriptions,
  useSubscriptionsDispatch,
} from '@features/subscriptions/context/subscription-context';
import { InputCheckbox, InputRadioGroup } from '@goodfynd/forms';
import { Alert, Button, FlexView, Link, Text } from '@goodfynd/ui';
import { addClassNames } from '@goodfynd/theme';
import { uniqBy } from 'lodash';
import type { PaymentMethod } from 'types/checkout';

import type {
  PaymentMethodFormFields,
  SubscriptionCheckoutPaymentMethodsProps,
} from './types';

import classes from './SubscriptionsCheckout.module.css';

export function SubscriptionCheckoutPaymentMethods({
  defaultPaymentMethod,
  savedPaymentMethods,
}: SubscriptionCheckoutPaymentMethodsProps) {
  const [isChangingPaymentMethod, setIsChangingPaymentMethod] = useState(
    !!!defaultPaymentMethod?.id
  );
  const [newPaymentMethod, setNewPaymentMethod] = useState<PaymentMethod>();

  const {
    cart: { paymentMethodId },
  } = useSubscriptions();
  const { setPaymentMethod } = useSubscriptionsDispatch();
  const { control, register, setValue } = useForm<PaymentMethodFormFields>({
    defaultValues: {
      paymentMethodId: defaultPaymentMethod?.id ?? '',
    },
  });

  const paymentMethods = useMemo(() => {
    return [
      ...savedPaymentMethods,
      ...(newPaymentMethod ? [newPaymentMethod] : []),
    ];
  }, [newPaymentMethod, savedPaymentMethods]);

  const paymentMethod = useMemo(
    () =>
      paymentMethods.find(
        (paymentMethod) => paymentMethod.id === paymentMethodId
      ),
    [paymentMethodId, paymentMethods]
  );

  const isDefaultInput = useMemo(() => {
    return {
      ...register('isDefault', {
        onChange: (e) => {
          setPaymentMethod(paymentMethod?.id ?? '', e.target.value);
        },
      }),
      ref: undefined,
    };
  }, [paymentMethod?.id, register, setPaymentMethod]);

  const paymentMethodInput = useMemo(() => {
    return {
      ...register('paymentMethodId', {
        onChange: (e) => {
          setValue('isDefault', false);

          setPaymentMethod(
            e.target.value === 'add-new-card' ? '' : e.target.value
          );
        },
      }),
      ref: undefined,
    };
  }, [register, setPaymentMethod, setValue]);

  return (
    <FlexView
      className={classes['payment-method-container']}
      corners="Medium"
      direction="column"
      gap={0}
      width="100%"
    >
      {!isChangingPaymentMethod && (
        <div className={classes['payment-method']}>
          <div>
            <Text as="body2">Pay with</Text>
            <Text>{paymentMethod?.display}</Text>
          </div>

          <Link
            className="link-underline"
            color="TextIconSecondary"
            href=""
            onClick={() => setIsChangingPaymentMethod(true)}
            value="Change"
          />
        </div>
      )}

      <div
        className={addClassNames(classes['change-payment-methods-container'], {
          [classes.hidden]: !isChangingPaymentMethod,
        })}
      >
        <FlexView direction="column" gap={0} width="100%" wrap="nowrap">
          <Text as="sectionHeader">Payment</Text>

          <InputRadioGroup
            control={control}
            className={addClassNames({
              [classes['payment-methods']]: !paymentMethod?.isDefault,
            })}
            options={[
              ...uniqBy(
                paymentMethods.map((paymentMethod) => ({
                  label: paymentMethod.display,
                  value: paymentMethod.id,
                })),
                'label'
              ),
              {
                label: 'Add new card',
                value: 'add-new-card',
              },
            ]}
            {...paymentMethodInput}
          />

          {paymentMethod?.id && !paymentMethod.isDefault && (
            <InputCheckbox
              control={control}
              label="Set as default"
              {...isDefaultInput}
            />
          )}

          <div
            className={addClassNames(classes['new-card-container'], {
              [classes.hidden]: !!paymentMethodId,
            })}
          >
            <StripeCardField
              onComplete={(newPaymentMethodId, display) =>
                setNewPaymentMethod({ id: newPaymentMethodId, display })
              }
            />
          </div>

          {(!!newPaymentMethod?.id || !!paymentMethodId) && (
            <Button
              content="Save"
              fullWidth
              rank="secondary"
              size="small"
              onClick={() => {
                if (newPaymentMethod?.id && !paymentMethodId) {
                  setPaymentMethod(newPaymentMethod.id);
                  setValue('paymentMethodId', newPaymentMethod.id);
                }

                setIsChangingPaymentMethod(false);
              }}
            />
          )}

          {!paymentMethodId && (
            <Alert status="info">
              <Text as="body2">
                No payment method selected. Please add or select a payment
                method to continue.
              </Text>
            </Alert>
          )}
        </FlexView>
      </div>
    </FlexView>
  );
}
